/**
 * LoomSpine — the run's section list; the mid-run surface.
 *
 * Header: "Drafting · N sections" (Drafted / Paused / Run failed after
 * the fact), a mono per-state tally ("1 done · 1 drafting · 5 queued") and
 * a thin violet progress bar (settled / total). With no running row to
 * carry it, one mono live-activity strip shows the newest agent event.
 * Rows: mono section number, title, a status chip + quiet meta line under
 * the title, and a right-hand status mark. A running row's chip carries a
 * 4-segment stage strip (Sources → Drafting → Review → Done) and the row
 * adds its newest agent action, so parallel sections each show live
 * detail in place.
 * Click a row → fires onSectionFocus (opens the Inspector).
 *
 * Status marks (launch-film Spine):
 *   queued          — empty slot (keeps the column aligned)
 *   drafting/audit  — violet spinner (static when paused)
 *   drafted         — green disc with a white check
 *   needs_approval  — amber disc with "!", pulse, popout below
 *   flagged         — red disc with "!"; failed drafting = red ring "✕"
 *   skipped         — faded dashed ring
 *
 * Animations honour ``prefers-reduced-motion``.
 *
 * SRP: pure presentation. Receives sections + activeSectionId via
 * props. Emits onSectionFocus on click. Doesn't subscribe to SSE,
 * doesn't fetch, doesn't dispatch.
 *
 * Phase 2 boundary: this component renders the spine. The Inspector
 * (Phase 3-4) opens INLINE under a clicked node and uses the same
 * onSectionFocus contract. The approval popout (Phase 5) renders
 * under a node when its status is needs_approval.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import { Button, Icon, type PillStatus, ProgressBar } from "../../ds/components";
import React, { useCallback, useEffect, useRef, useState } from "react";
import type { SpineCounters } from "../eventLog";
import type { SpineSection } from "../hooks/useVariantCRun";
import {
	type AgentActivityLine,
	type AgentActivityMap,
	hasAnyActivity,
	latestActivityAcross,
} from "../services/agentActivityFeed";
import { type LiveDocLoadMap, hasActiveLiveDocLoad } from "../services/liveDocLoadTracker";
import type { InitProgress, InitStage } from "../sseAdapter";
import { formatGutterNumber } from "../sectionLabel";
import {
	DRAFT_STEPS,
	type DraftStep,
	deriveDraftStep,
	deriveSpineProgress,
	estimateTimeLeft,
	sublineDetail,
} from "../services/spineProgress";
import { formatTokens } from "../utils/formatTokens";
import { spineDepth } from "../utils/spineOrder";

/** Title indent per hierarchy level (px). Small: the taskpane is narrow. */
const SPINE_INDENT_PX = 8;
import type { PendingApproval } from "../state";
import { brandColors, bubbleShape, fonts, inkColors, motion, statusColors, surfaceColors } from "../tokens";
import ApprovalPopout from "./ApprovalPopout";
import SpineTagStrip from "./SpineTagStrip";

interface LoomSpineProps {
	sections: SpineSection[];
	activeSectionId: string | null;
	/** Fired when the writer clicks any node — host opens the inspector. */
	onSectionFocus?: (sectionId: string) => void;
	/** Fired when the writer clicks a section's ``N⚖`` regulator badge — host
	 *  focuses the section AND opens the inspector's Review tab so the persisted
	 *  regulatory findings (mini-reports) are shown without re-running the audit. */
	onOpenReview?: (sectionId: string) => void;
	/** Streamed connection error, if any (renders as a top banner). */
	streamError?: string | null;
	/** Pending approval, if any. Rendered as an inline popout under the
	 *  matching section's spine row. Null when no approval is pending. */
	pendingApproval?: PendingApproval | null;
	/** Writer accepted the approval. Host dispatches APPROVAL_RESOLVED
	 *  and flips the section status back to drafted. */
	onApprovalAccept?: () => void;
	/** Writer wants to inspect the findings. Host opens the inspector
	 *  for that section + Review tab. */
	onApprovalOpenReview?: () => void;
	/** Per-section cumulative event counters sourced from
	 *  ``state.eventLog.counters``. Absent section ⇒ no pill rendered
	 *  (SpineTagStrip also self-hides on total=0). Owned by the shell;
	 *  LoomSpine is a pure consumer per Spec 17's single-writer rule. */
	countersBySection?: ReadonlyMap<string, SpineCounters>;
	/** Initialization progress emitted while the backend runner is being
	 *  built (loading libraries, indexing documents). Rendered as a stage
	 *  checklist panel with a determinate progress bar when the backend
	 *  sends counts. Null / undefined once the run starts. */
	initStatus?: InitProgress | null;
	/** Muted secondary line under the init panel for work running in
	 *  parallel with startup (the regulatory audit-library build). Only
	 *  shown while ``initStatus`` is set. */
	initSideNote?: string | null;
	/** When true, preserve status markers but suppress motion effects
	 *  (pulse, motes, elapsed ticking) so paused runs appear static. */
	pauseAnimations?: boolean;
	/** Per-section "loading into Word…" tracker, keyed by section id. Present
	 *  entry ⇒ the section's redraft is being written into the live doc
	 *  (``loading``) or the write found no matching heading (``failed``). Owned
	 *  by the shell (VariantCShell) and cleared on a confirmed write. */
	liveDocLoadBySection?: LiveDocLoadMap;
	/** Per-section LIVE agent activity feed — the last few tool calls / retrieval
	 *  steps the agent made, indented by sub-agent depth. Shell-owned (survives
	 *  the section_state_changed subline wipe). Rendered under the active
	 *  drafting row in place of the single-line subline. */
	agentActivityBySection?: AgentActivityMap;
	/** Run-level latest activity line for the header's live-activity strip —
	 *  shell-owned (``nextRunActivity`` fold, reset on run start) so it outlives
	 *  the per-section feed clears and survives a tab switch. When omitted the
	 *  strip falls back to the newest line across ``agentActivityBySection``,
	 *  remembered for as long as this component stays mounted. */
	latestRunActivity?: AgentActivityLine | null;
	/** The run itself failed (status ``failed``) — the header reads
	 *  "Run failed · N sections" instead of Drafting / Drafted. */
	runFailed?: boolean;
	/** The finished run's wall-clock duration from the run summary. When set,
	 *  the header's total reads it instead of the times this pane observed. */
	runDurationMs?: number;
	/** Sections in the whole doctype template. When the run covers fewer, the
	 *  header says "N of M template sections" so a scoped run still shows the
	 *  document's full size (Guru 2026-10-14). */
	templateSectionCount?: number | null;
	/** Run controls (Pause · Stop run …) shown under the progress bar. */
	controls?: React.ReactNode;
	/** Quiet note pinned under the list while the run drafts. */
	footerNote?: string | null;
	/** Finished run (design board 14): the header becomes a summary card —
	 *  how many sections are in the document, citations, open issues — with
	 *  the way into the Issues tab. Null/undefined keeps the progress header. */
	/** Replaces the progress header (a regenerate's own controls). */
	header?: React.ReactNode;
	/** Shown under the rows. */
	afterRows?: React.ReactNode;
	complete?: {
		issuesOpen: number | null;
		onReviewIssues?: () => void;
	} | null;
	/** Sections of a running fix pass — their rows read "Fixing", not "Drafting". */
	fixingModuleIds?: ReadonlySet<string>;
	/** Flags in that pass, for the header ("Fixing 3 flags in 1 section"). */
	fixingFlagCount?: number;
}

// ---------------------------------------------------------------------------
// Init checklist derivation
// ---------------------------------------------------------------------------

export interface InitChecklistStep {
	id: "connect" | "verify" | "finalize";
	label: string;
	state: "done" | "active" | "pending";
	/** Determinate counts — only set while an older server reports its
	 *  document-tree ``indexing`` stage (current servers don't preload). */
	progress?: { done: number; total: number };
}

const INIT_STEP_DEFS: ReadonlyArray<{
	id: InitChecklistStep["id"];
	label: string;
}> = [
	{ id: "connect", label: "Connecting to libraries" },
	{ id: "verify", label: "Checking source retrieval" },
	{ id: "finalize", label: "Preparing drafting agent" },
];

const STAGE_TO_STEP: Record<InitStage, number> = {
	// Supervisor-side subprocess boot, before the runner exists — render as the
	// message-only banner (checklist returns null), not a checklist step: nothing
	// is connecting to libraries yet. The real checklist takes over once
	// the child emits its first ``libraries`` init_progress.
	booting: -1,
	preparing: 0,
	libraries: 0,
	table_spec: 1,
	health_check: 1,
	// No longer emitted — document trees aren't preloaded at run start (the
	// retrieval agent fetches outlines lazily). An older server's indexing
	// phase folds into the retrieval-check step.
	indexing: 1,
	finalizing: 2,
	// Activation resolution happens after the checklist completes — render
	// as the message-only banner (checklist returns null), not a step.
	activation: -1,
	unknown: -1,
};

/**
 * Map the latest init event to the 3-step checklist. Pure — every
 * backend init event is self-describing (full stage + counts), so no
 * accumulation across events is needed; the panel is always correct
 * from any single event, including after an SSE reconnect mid-init.
 *
 * Returns null for the "unknown" stage (older server without
 * init_stage) — the caller falls back to a message-only banner.
 */
export function deriveInitChecklist(init: InitProgress): InitChecklistStep[] | null {
	const activeIdx = STAGE_TO_STEP[init.stage];
	if (activeIdx < 0) return null;
	return INIT_STEP_DEFS.map((def, i) => {
		const state: InitChecklistStep["state"] = i < activeIdx ? "done" : i === activeIdx ? "active" : "pending";
		const step: InitChecklistStep = { id: def.id, label: def.label, state };
		if (
			i === activeIdx &&
			init.stage === "indexing" &&
			typeof init.done === "number" &&
			typeof init.total === "number" &&
			init.total > 0
		) {
			step.progress = { done: init.done, total: init.total };
		}
		return step;
	});
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
	root: {
		flex: 1,
		minHeight: 0,
		position: "relative",
		background: "var(--surface)",
		overflow: "hidden",
		display: "flex",
		flexDirection: "column",
	},
	viewport: {
		flex: 1,
		minHeight: 0,
		overflow: "auto",
		position: "relative",
	},

	errorBanner: {
		position: "sticky",
		top: 0,
		margin: "0 12px 8px",
		padding: "6px 10px",
		background: "var(--danger-soft)",
		border: "1px solid color-mix(in srgb, var(--danger) 35%, transparent)",
		borderRadius: "3px",
		fontSize: "11px",
		color: "var(--danger)",
		zIndex: 4,
	},
	// Init panel — stage checklist shown while the backend builds the
	// runner (library profiles → retrieval check → document indexing →
	// prompt assembly). Same sticky slot + surface recipe as the old
	// single-line banner; removed in one place when run_metadata clears
	// the initStatus prop.
	initPanel: {
		position: "sticky",
		top: 0,
		margin: "0 12px 8px",
		padding: "8px 10px",
		background: surfaceColors.paper,
		border: `1px solid ${surfaceColors.lineStrong}`,
		borderRadius: "3px",
		fontSize: "11px",
		color: inkColors.ink3,
		zIndex: 4,
	},
	initPanelTitle: {
		display: "flex",
		alignItems: "center",
		gap: "6px",
		fontFamily: fonts.mono,
		fontSize: "10px",
		letterSpacing: "0.06em",
		textTransform: "uppercase",
		color: inkColors.ink3,
		marginBottom: "4px",
	},
	initBannerIcon: {
		opacity: 0.5,
	},
	initStep: {
		display: "flex",
		alignItems: "center",
		gap: "6px",
		padding: "2px 0",
	},
	initStepLabel: {
		color: inkColors.ink3,
	},
	initStepPending: {
		color: inkColors.ink4,
	},
	initSideNote: {
		marginTop: "6px",
		fontSize: "11px",
		color: inkColors.ink4,
	},
	initStepCount: {
		marginLeft: "auto",
		fontFamily: fonts.mono,
		fontSize: "10px",
		color: inkColors.ink4,
	},
	initStepMarker: {
		flexShrink: 0,
		width: "6px",
		height: "6px",
		borderRadius: "3px",
		border: `1px solid ${inkColors.ink5}`,
		background: "transparent",
	},
	initStepMarkerDone: {
		border: "none",
		background: statusColors.cool,
	},
	initStepMarkerActive: {
		border: "none",
		background: brandColors.mid,
		boxShadow: `0 0 6px ${brandColors.glow}`,
		animationName: {
			"0%": { opacity: 1 },
			"50%": { opacity: 0.45 },
			"100%": { opacity: 1 },
		},
		animationDuration: "1600ms",
		animationTimingFunction: "ease-in-out",
		animationIterationCount: "infinite",
		"@media (prefers-reduced-motion: reduce)": {
			animation: "none",
		},
	},
	initBar: {
		height: "3px",
		marginTop: "4px",
		borderRadius: "2px",
		background: surfaceColors.line,
		overflow: "hidden",
	},
	initBarFill: {
		height: "100%",
		background: `linear-gradient(to right, ${brandColors.primary}, ${statusColors.cool})`,
		transitionProperty: "width",
		transitionDuration: "300ms",
		transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
		"@media (prefers-reduced-motion: reduce)": {
			transition: "none",
		},
	},
	emptyHint: {
		padding: "40px 16px",
		fontFamily: fonts.body,
		fontSize: "12px",
		color: inkColors.ink4,
		textAlign: "center",
	},
	// Run header — "Drafting · N sections", thin progress bar, live-activity
	// strip (launch-film Spine). Sits above the rows in normal flow.
	header: {
		padding: "4px 16px 12px",
	},
	headerTitle: {
		display: "flex",
		alignItems: "baseline",
		gap: "8px",
		fontFamily: fonts.body,
		fontSize: "15px",
		fontWeight: 600,
		color: inkColors.ink,
		letterSpacing: "-0.1px",
	},
	headerTime: {
		marginLeft: "auto",
		fontFamily: fonts.mono,
		fontSize: "10px",
		fontWeight: 500,
		letterSpacing: "0.04em",
		color: inkColors.ink3,
		fontVariantNumeric: "tabular-nums",
		whiteSpace: "nowrap",
	},
	headerTimeNote: {
		display: "inline-flex",
		verticalAlign: "-2px",
		marginLeft: "3px",
		color: inkColors.ink3,
		cursor: "help",
	},
	rowTotal: {
		flexShrink: 0,
		alignSelf: "center",
		fontFamily: fonts.mono,
		fontSize: "10px",
		color: inkColors.ink3,
		fontVariantNumeric: "tabular-nums",
		whiteSpace: "nowrap",
	},
	headerFailedMeta: {
		fontFamily: fonts.mono,
		fontSize: "10px",
		fontWeight: 500,
		letterSpacing: "0.6px",
		textTransform: "uppercase",
		color: statusColors.danger,
	},
	progressTrack: {
		height: "3px",
		marginTop: "10px",
		borderRadius: "2px",
		background: brandColors.light,
		overflow: "hidden",
	},
	progressFill: {
		height: "100%",
		borderRadius: "2px",
		background: brandColors.primary,
		transitionProperty: "width",
		transitionDuration: "300ms",
		transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
		"@media (prefers-reduced-motion: reduce)": {
			transition: "none",
		},
	},
	headerSummary: {
		marginTop: "4px",
		fontFamily: fonts.mono,
		fontSize: "10px",
		letterSpacing: "0.04em",
		color: inkColors.ink4,
		fontVariantNumeric: "tabular-nums",
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	// "Now drafting" card — the running section, its stage and its live log.

	nowLogText: {
		flex: 1,
		minWidth: 0,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	nowLogAge: {
		flexShrink: 0,
		fontSize: "9.5px",
		color: inkColors.ink5,
		fontVariantNumeric: "tabular-nums",
	},

	// Row status chip + quiet meta line under the title.
	rowMeta: {
		display: "flex",
		alignItems: "center",
		gap: "6px",
		marginTop: "3px",
		minWidth: 0,
	},
	rowMetaText: {
		minWidth: 0,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		fontFamily: fonts.mono,
		fontSize: "10px",
		fontWeight: 400,
		color: inkColors.ink4,
		fontVariantNumeric: "tabular-nums",
	},
	rowMetaLive: {
		color: inkColors.ink3,
	},
	// Running row: a 3-dash stage strip inside the pill (Sources →
	// Drafting → Review) and the newest agent action under it.
	rowSteps: {
		display: "inline-flex",
		gap: "2px",
		alignItems: "center",
	},
	rowStep: {
		width: "7px",
		height: "3px",
		borderRadius: "2px",
		background: "var(--line)",
	},
	rowStepDone: {
		background: "var(--success)",
	},
	rowStepActive: {
		background: "var(--brand)",
	},
	rowLog: {
		display: "flex",
		alignItems: "baseline",
		gap: "6px",
		marginTop: "3px",
		minWidth: 0,
		fontFamily: fonts.mono,
		fontSize: "10px",
		fontWeight: 400,
		color: inkColors.ink3,
	},
	rowMetaApproval: {
		color: statusColors.warn,
		fontWeight: 500,
		textTransform: "uppercase",
		letterSpacing: "0.2px",
		fontFamily: fonts.body,
	},
	chip: {
		display: "inline-flex",
		alignItems: "center",
		flexShrink: 0,
		height: "15px",
		padding: "0 5px",
		borderRadius: "4px",
		border: `1px solid ${surfaceColors.line}`,
		fontFamily: fonts.mono,
		fontSize: "9px",
		fontWeight: 600,
		letterSpacing: "0.06em",
		textTransform: "uppercase",
		lineHeight: 1,
		color: inkColors.ink4,
		whiteSpace: "nowrap",
	},
	chipLive: {
		color: brandColors.primary,
		background: brandColors.tint,
		border: `1px solid ${brandColors.light}`,
	},
	chipDone: {
		color: inkColors.ink3,
		background: `color-mix(in srgb, ${statusColors.cool} 10%, transparent)`,
		border: `1px solid color-mix(in srgb, ${statusColors.cool} 40%, transparent)`,
	},
	chipApproval: {
		color: inkColors.ink2,
		background: `color-mix(in srgb, ${statusColors.warn} 10%, transparent)`,
		border: `1px solid color-mix(in srgb, ${statusColors.warn} 50%, transparent)`,
	},
	chipDanger: {
		color: statusColors.danger,
		background: `color-mix(in srgb, ${statusColors.danger} 6%, transparent)`,
		border: `1px solid color-mix(in srgb, ${statusColors.danger} 30%, transparent)`,
	},
	chipSkipped: {
		color: inkColors.ink5,
		border: `1px dashed ${surfaceColors.lineStrong}`,
	},
	activityStrip: {
		marginTop: "12px",
		padding: "7px 10px",
		borderRadius: "6px",
		background: brandColors.tint,
		border: `1px solid ${brandColors.light}`,
		fontFamily: fonts.mono,
		fontSize: "11px",
		color: brandColors.primary,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	activityStripError: {
		color: statusColors.danger,
	},
	row: {
		display: "flex",
		alignItems: "center",
		gap: "10px",
		padding: "9px 16px",
		position: "relative",
		cursor: "pointer",
		background: "none",
		border: "none",
		borderBottom: `1px solid ${surfaceColors.line}`,
		width: "100%",
		textAlign: "left",
		":hover": {
			background: brandColors.tint,
		},
	},
	rowActive: {
		background: brandColors.tint,
	},
	// # DECISION: rows that have something to open (running or drafted) read
	// #   as clickable: a trailing chevron that turns violet and nudges right on
	// #   hover, a pressed tint, and a focus ring. Queued / skipped rows keep
	// #   no chevron, a default cursor and no hover tint, so they read as inert.
	// # WHY: Guru (2026-10-14) — "the UI should implicitly convey that each
	// #      section … is clickable, otherwise people have no clue."
	// # DATE: 2026-10-14
	rowOpenable: {
		":hover": {
			background: brandColors.light,
		},
		"&:active": {
			background: `color-mix(in srgb, ${brandColors.mid} 20%, transparent)`,
		},
		"&:focus-visible": {
			outline: `2px solid ${brandColors.primary}`,
			outlineOffset: "-2px",
		},
		"&:hover [data-row-chevron]": {
			color: brandColors.primary,
			transform: "translateX(2px)",
		},
		"&:focus-visible [data-row-chevron]": {
			color: brandColors.primary,
		},
	},
	rowInert: {
		cursor: "default",
		":hover": {
			background: "none",
		},
	},
	rowChevron: {
		flexShrink: 0,
		marginLeft: "-4px",
		marginRight: "-6px",
		color: inkColors.ink5,
		transitionProperty: "transform, color",
		transitionDuration: "150ms",
		"@media (prefers-reduced-motion: reduce)": {
			transition: "none",
		},
	},
	rowChevronActive: {
		color: brandColors.primary,
	},
	// Keeps the column aligned on rows without a chevron.
	rowChevronSlot: {
		flexShrink: 0,
		width: "12px",
		marginLeft: "-4px",
		marginRight: "-6px",
	},
	// # DECISION: rows being drafted stand out as a soft brand-tinted band
	// #   with a 3px brand rail, a brand-coloured number and a bold brand
	// #   section timer. Queued and drafted rows stay on the plain paper.
	// # WHY: Guru (2026-10-14) — "make the sections that are being drafted
	// #      more prominent - maybe the background can be a bit different".
	// #      Brand tokens only, so the Indegene white-label tints blue.
	// # DATE: 2026-10-14
	rowRunning: {
		background: brandColors.light,
		boxShadow: `inset 3px 0 0 ${brandColors.primary}`,
		borderBottomColor: `color-mix(in srgb, ${brandColors.mid} 25%, transparent)`,
		":hover": {
			background: `color-mix(in srgb, ${brandColors.mid} 15%, transparent)`,
		},
		"& [data-gutter]": {
			color: brandColors.primary,
			fontWeight: 600,
		},
	},
	// Running titles may wrap to two lines rather than truncate early.
	labelRunning: {
		whiteSpace: "normal",
	},
	rowTotalRunning: {
		fontSize: "11px",
		fontWeight: 700,
		color: brandColors.primary,
	},
	rowRecede: {
		/* Recede effect removed — spine now remains static during drafting */
	},
	numberGutter: {
		// Left-aligned mono number column. Wide enough for CMC Module 3
		// numbers ("3.2.S.1.2", ~54px in 10px mono); shorter CSR / CER
		// numbers ("9.1", "13.2") leave whitespace. ``overflow: hidden`` is
		// defensive: longer numbers truncate rather than push the title.
		width: "62px",
		overflow: "hidden",
		fontSize: "10px",
		letterSpacing: "0.2px",
		color: inkColors.ink4,
		textAlign: "left",
		fontVariantNumeric: "tabular-nums",
		fontFamily: fonts.mono,
		flexShrink: 0,
	},
	// Right-hand status mark: spinner (drafting) / green check (drafted) /
	// empty (queued) / the existing failed, flagged, approval glyphs.
	dotWrap: {
		position: "relative",
		width: "18px",
		height: "18px",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		flexShrink: 0,
	},
	dot: {
		borderRadius: "50%",
		transitionProperty: "all",
		transitionDuration: `${motion.transitionMs}ms`,
		zIndex: 2,
		"@media (prefers-reduced-motion: reduce)": {
			transition: "none",
		},
	},
	dotCrystallize: {
		animationName: {
			"0%": { transform: "scale(0.7)", opacity: 0 },
			"100%": { transform: "scale(1)", opacity: 1 },
		},
		animationDuration: "350ms",
		animationTimingFunction: "cubic-bezier(0.68, -0.55, 0.265, 1.55)",
		"@media (prefers-reduced-motion: reduce)": {
			animation: "none",
		},
	},
	// Queued — nothing drawn; the slot keeps the column aligned.
	dotQueued: {
		width: "16px",
		height: "16px",
	},
	// Conditional activation skipped this module (condition FALSE) — a
	// faded dashed ring so it reads as "deliberately not drafted" rather
	// than "still waiting".
	dotSkipped: {
		width: "12px",
		height: "12px",
		background: "var(--surface-raised)",
		border: `1.5px dashed ${surfaceColors.lineStrong}`,
		opacity: 0.55,
	},
	dotDrafting: {
		width: "12px",
		height: "12px",
		border: `2px solid ${brandColors.light}`,
		borderTopColor: brandColors.primary,
		animationName: {
			"0%": { transform: "rotate(0deg)" },
			"100%": { transform: "rotate(360deg)" },
		},
		animationDuration: "900ms",
		animationTimingFunction: "linear",
		animationIterationCount: "infinite",
		"@media (prefers-reduced-motion: reduce)": {
			animation: "none",
		},
	},
	// Paused runs keep the marker but stop the spin.
	dotStatic: {
		animation: "none",
	},
	dotDrafted: {
		width: "16px",
		height: "16px",
		background: statusColors.cool,
		color: "var(--surface)",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	dotApproval: {
		width: "16px",
		height: "16px",
		background: statusColors.warn,
		color: "var(--surface)",
		fontSize: "10px",
		fontWeight: 800,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	// Drafting FAILED (server module_error) — no body was produced. Hollow
	// red ring with a ✕ so it reads differently from a review flag's "!".
	dotFailed: {
		width: "14px",
		height: "14px",
		background: "var(--surface-raised)",
		border: `1.5px solid ${statusColors.danger}`,
		color: statusColors.danger,
		fontSize: "8px",
		fontWeight: 800,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		lineHeight: 1,
	},
	dotFlagged: {
		width: "16px",
		height: "16px",
		background: statusColors.danger,
		color: "var(--surface)",
		fontSize: "10px",
		fontWeight: 800,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	pulse: {
		position: "absolute",
		inset: "0px",
		borderRadius: "50%",
		animationName: {
			"0%": { boxShadow: "0 0 0 0px currentColor", opacity: 0.6 },
			"70%": { boxShadow: "0 0 0 6px currentColor", opacity: 0 },
			"100%": { boxShadow: "0 0 0 6px currentColor", opacity: 0 },
		},
		animationDuration: `${motion.pulseDurationMs}ms`,
		animationIterationCount: "infinite",
		animationTimingFunction: "ease-out",
		"@media (prefers-reduced-motion: reduce)": {
			animation: "none",
		},
	},
	pulseWarn: { color: `color-mix(in srgb, ${statusColors.warn} 40%, transparent)` },
	label: {
		flex: 1,
		minWidth: 0,
		fontFamily: fonts.body,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	labelQueued: {
		fontSize: "13px",
		fontWeight: 400,
		color: inkColors.ink3,
	},
	labelDone: {
		fontSize: "13px",
		fontWeight: 400,
		color: inkColors.ink,
	},
	labelActive: {
		fontSize: "13px",
		fontWeight: 600,
		color: inkColors.ink,
	},
	subline: {
		fontSize: "10px",
		fontWeight: 400,
		color: inkColors.ink3,
		marginTop: "2px",
		fontFamily: fonts.mono,
		display: "flex",
		alignItems: "center",
		gap: "6px",
	},
	sublineDot: {
		color: brandColors.primary,
	},
	sublineElapsed: {
		// Quieter than the subline text — the timer is supporting info,
		// not the headline. Same mono font for tabular numerics so the
		// counter doesn't jitter as digits change.
		color: inkColors.ink4,
		fontVariantNumeric: "tabular-nums",
		flexShrink: 0,
	},
	// Spec 23 P5 — persistent subline on drafted sections with
	// accepted-time / word-count / citations / quality. Quieter than
	// the active-drafting subline (monochrome ink4; no hot-orange
	// leading dot) since it's a post-work summary rather than a live
	// status beat.
	sublineDrafted: {
		fontSize: "10px",
		fontWeight: 400,
		color: inkColors.ink4,
		marginTop: "2px",
		fontFamily: fonts.mono,
		marginLeft: "6px",
		fontVariantNumeric: "tabular-nums",
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	// Danger-toned subline for a redraft that couldn't be written into the live
	// doc (no matching heading — the doc likely needs a full Load).
	activityFeed: {
		display: "block",
		marginTop: "2px",
	},
	activityLine: {
		fontSize: "10px",
		fontWeight: 400,
		color: inkColors.ink4,
		fontFamily: fonts.mono,
		lineHeight: "1.35",
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	activityLineLive: {
		color: inkColors.ink3,
	},
	activityLineError: {
		color: statusColors.danger,
		fontWeight: 500,
	},
	activityElapsed: {
		color: inkColors.ink5,
		fontVariantNumeric: "tabular-nums",
	},
	sublineLoadFailed: {
		fontSize: "10px",
		fontWeight: 500,
		color: statusColors.danger,
		marginTop: "2px",
		marginLeft: "6px",
		fontFamily: fonts.body,
		letterSpacing: "0.2px",
	},
	/** Transient "landed in Word" confirmation. Same metrics as its failed
	 *  sibling so the row height doesn't shift as the tick appears/expires. */
	sublineLoaded: {
		fontSize: "10px",
		fontWeight: 500,
		color: statusColors.cool,
		marginTop: "2px",
		marginLeft: "6px",
		fontFamily: fonts.body,
		letterSpacing: "0.2px",
	},
	rightMeta: {
		fontSize: "9px",
		color: inkColors.ink5,
		fontFamily: fonts.mono,
		flexShrink: 0,
	},
	rightMetaWarn: { color: statusColors.warn, fontWeight: 700 },
	// Spec 23 P6 — per-quality / per-findings rightMeta variants.
	// rightMetaLow is styled like rightMetaDanger (red, bold uppercase)
	// because a low-quality drafted section warrants writer attention;
	// rightMetaFindings uses warn (amber) because findings are advisory.
	rightMetaLow: {
		color: statusColors.danger,
		fontWeight: 700,
		letterSpacing: "0.5px",
	},
	rightMetaFindings: {
		color: statusColors.warn,
		fontWeight: 700,
		fontVariantNumeric: "tabular-nums",
	},
	rightMetaContentGap: {
		color: statusColors.danger,
		fontWeight: 700,
		fontVariantNumeric: "tabular-nums",
	},
	// Regulatory-audit flag count — blue to match the [REGULATOR FLAG: …]
	// inline marker styling and distinguish it from the red content-gap badge.
	rightMetaRegulator: {
		color: "var(--info)",
		fontWeight: 700,
		fontVariantNumeric: "tabular-nums",
	},
	floatingDock: {
		position: "absolute",
		left: "12px",
		right: "12px",
		bottom: "8px",
		zIndex: 6,
		pointerEvents: "none",
	},
	floatingRow: {
		pointerEvents: "auto",
		paddingLeft: "0",
		paddingRight: "0",
		borderRadius: "8px",
		background: "var(--surface-raised)",
		border: `1px solid ${surfaceColors.lineStrong}`,
		boxShadow: "0 6px 20px rgba(17,24,39,0.12)",
	},
	floatingNumberGutter: {
		marginLeft: "0",
		width: "auto",
		textAlign: "left",
	},
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Format the "accepted 2:14pm" time label from an epoch-ms timestamp.
 * Pure; testable. Uses the same lowercase-am/pm, no-leading-zero
 * grammar as TimelineView's formatEventTime so the cadence feels
 * consistent across surfaces.
 */
export function formatAcceptedAt(ts: number): string {
	const d = new Date(ts);
	const hour12 = d.toLocaleString("en-US", {
		hour: "numeric",
		minute: "2-digit",
		hour12: true,
	});
	return `accepted ${hour12.replace(/\s?(AM|PM)/i, (_, m) => m.toLowerCase())}`;
}

/** Pure — build the drafted-section subline from a SpineSection's
 *  post-work fields. Returns null when there is nothing to render
 *  (no acceptedAt, no wordCount, no citationCount, no quality —
 *  caller omits the subline entirely).
 *
 *  Format: "accepted Xpm · N words · K citations · quality Y".
 *  Missing fields drop out; the " · " separator logic preserves
 *  clean output regardless of which fields are present. */
export function formatDraftedSubline(section: {
	acceptedAt?: number;
	wordCount?: number;
	citationCount?: number;
	quality?: "high" | "medium" | "low";
	inputTokens?: number;
	outputTokens?: number;
	model?: string | null;
}): string | null {
	const parts: string[] = [];
	if (section.acceptedAt) {
		parts.push(formatAcceptedAt(section.acceptedAt));
	}
	if (section.wordCount !== undefined && section.wordCount > 0) {
		parts.push(`${section.wordCount} words`);
	}
	if (section.citationCount !== undefined && section.citationCount > 0) {
		parts.push(`${section.citationCount} citation${section.citationCount === 1 ? "" : "s"}`);
	}
	if (section.quality) {
		parts.push(`quality ${section.quality}`);
	}
	// Per-section token usage + drafting model backend. Only shown once the
	// persisted telemetry is present (0/undefined on older runs).
	const inTok = section.inputTokens ?? 0;
	const outTok = section.outputTokens ?? 0;
	if (inTok > 0 || outTok > 0) {
		parts.push(`${formatTokens(inTok)} in · ${formatTokens(outTok)} out`);
	}
	if (section.model) {
		parts.push(section.model);
	}
	return parts.length > 0 ? parts.join(" · ") : null;
}

/**
 * Format a millisecond duration as the design's compact "Xs" / "Xm Ys"
 * shape. Sub-second precision below 10s ("2.1s") so the counter feels
 * live; whole-second above ("12s"); minutes after a minute ("1m 03s").
 * Pure function — testable in isolation.
 */
function formatElapsed(ms: number): string {
	if (ms < 0) return "0s";
	const totalSeconds = ms / 1000;
	if (totalSeconds < 10) {
		return `${totalSeconds.toFixed(1)}s`;
	}
	if (totalSeconds < 60) {
		return `${Math.floor(totalSeconds)}s`;
	}
	const minutes = Math.floor(totalSeconds / 60);
	const remSeconds = Math.floor(totalSeconds % 60);
	return `${minutes}m ${String(remSeconds).padStart(2, "0")}s`;
}

/** Whole-section / whole-run duration: "42s", "3m 05s", "1h 04m". */
function formatTotal(ms: number): string {
	const sec = Math.max(0, Math.floor(ms / 1000));
	if (sec < 60) return `${sec}s`;
	const min = Math.floor(sec / 60);
	if (min < 60) return `${min}m ${String(sec % 60).padStart(2, "0")}s`;
	return `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, "0")}m`;
}

/**
 * Right-hand row marker (design SectionRow): spinner while drafting (or while
 * a regulatory audit runs on the row), a check once drafted, nothing while
 * queued, and ✕ / ! for failed, flagged and needs-approval rows.
 */
function SpineStatusMark({
	status,
	failed,
	failureReason,
	auditing,
	paused,
}: {
	status: SpineSection["status"];
	failed?: boolean;
	failureReason?: string;
	auditing: boolean;
	paused: boolean;
}) {
	const isFailed = !!failed && status === "flagged";
	const markStatus: SpineSection["status"] = auditing && !isFailed ? "drafting" : status;
	const kind = isFailed ? "failed" : markStatus;
	return (
		<span className="as-srow-mark" data-status-mark={kind}>
			{isFailed ? (
				<span className="as-srow-mark-bad" title={`Drafting failed${failureReason ? `: ${failureReason}` : ""}`}>
					✕
				</span>
			) : markStatus === "drafting" ? (
				<span className={mergeClasses("as-spin", paused && "as-spin-paused")} aria-label="working" />
			) : markStatus === "drafted" ? (
				<span className="as-srow-mark-ok">
					<Icon name="check" />
				</span>
			) : markStatus === "flagged" ? (
				<span className="as-srow-mark-bad">!</span>
			) : markStatus === "needs_approval" ? (
				<span className="as-srow-mark-warn">!</span>
			) : null}
		</span>
	);
}

/** Newest feed lines shown in the "Now drafting" card's live log. */
/** Other running sections listed under the card before "+N more". */

/** Row status pill — the row's state in words (design StatusPill). */
function chipFor(section: SpineSection, fixing = false): { status: PillStatus; label: string } {
	// A fix pass re-drafts the section: its row reads "Fixing", not "Drafting".
	if (fixing && section.status === "drafting") return { status: "drafting", label: "Fixing" };
	if (section.failed && section.status === "flagged") return { status: "failed", label: "Failed" };
	if (section.auditPhase === "running") return { status: "review", label: "Reviewing" };
	// Post-draft work on a drafted section (review, then filling flags).
	if (section.postDraftTask === "review") return { status: "review", label: "Reviewing" };
	if (section.postDraftTask === "fill") return { status: "review", label: "Filling flags" };
	switch (section.status) {
		case "drafting":
			return { status: "drafting", label: "Drafting" };
		case "drafted":
			return { status: "done", label: "Done" };
		case "needs_approval":
			return { status: "paused", label: "Needs approval" };
		case "flagged":
			return { status: "failed", label: "Flagged" };
		case "skipped":
			return { status: "queued", label: "Skipped" };
		default:
			return { status: "queued", label: "Queued" };
	}
}

/** Pill wording for a running row's stage. */
const STAGE_LABEL: Record<DraftStep, string> = {
	sources: "Reading sources",
	drafting: "Drafting",
	reviewing: "Reviewing",
	done: "Done",
};
/** Spine header during a fix pass: "Fixing 3 flags in 1 section". */
export function fixingHeader(flags: number, sections: number): string {
	const what = flags > 0 ? `${flags} flag${flags === 1 ? "" : "s"}` : "flags";
	return `Fixing ${what} in ${sections} section${sections === 1 ? "" : "s"}`;
}

/** A feed line's text without the "Agent · " prefix the log shows as a tag. */
function lineBody(line: AgentActivityLine): string {
	const prefix = line.agent ? `${line.agent} · ` : "";
	return prefix && line.text.startsWith(prefix) ? line.text.slice(prefix.length) : line.text;
}

const isRunning = (s: SpineSection): boolean => s.status === "drafting" || s.auditPhase === "running";
/** Running, or post-draft work (a writer's review, the missing-info fill) on a
 *  drafted row — the row shows the live subline + timer either way, but only
 *  ``isRunning`` rows count as run progress / get the drafting stage dashes. */
const isLive = (s: SpineSection): boolean => isRunning(s) || s.postDraftTask !== undefined;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const LoomSpine: React.FC<LoomSpineProps> = ({
	sections,
	activeSectionId,
	onSectionFocus,
	onOpenReview,
	streamError,
	pendingApproval,
	onApprovalAccept,
	onApprovalOpenReview,
	countersBySection,
	initStatus,
	initSideNote,
	pauseAnimations = false,
	liveDocLoadBySection,
	agentActivityBySection,
	latestRunActivity,
	runFailed = false,
	runDurationMs,
	templateSectionCount,
	controls,
	footerNote = "Keep working in Word. Each section loads in at its heading as soon as it finishes.",
	complete = null,
	header = null,
	afterRows = null,
	fixingModuleIds,
	fixingFlagCount = 0,
}) => {
	const styles = useStyles();
	// A fix pass reuses the run's stream, but nothing is being drafted: the
	// header and the row tag say "Fixing", and the drafting tallies are hidden.
	const fixingPass = (fixingModuleIds?.size ?? 0) > 0;
	const activeIdx = sections.findIndex((s) => s.id === activeSectionId);
	const activeSection = activeSectionId === null ? null : (sections.find((s) => s.id === activeSectionId) ?? null);

	// Header progress — "Drafting · N sections" + bar fill. Settled share, not
	// the active row's index: modules draft concurrently and finish out of
	// document order, so an index-based fill would jump backwards.
	const progress = deriveSpineProgress(sections, { paused: pauseAnimations, runFailed });
	// Time left: the pace sections settle at since this pane first saw the run
	// drafting. Reset when the run stops drafting or the section count changes.
	const paceBase = useRef<{ at: number; done: number; total: number } | null>(null);
	if (progress.phase !== "drafting" || fixingPass) paceBase.current = null;
	else if (!paceBase.current || paceBase.current.total !== progress.total || progress.done < paceBase.current.done)
		paceBase.current = { at: Date.now(), done: progress.done, total: progress.total };
	const timeLeft =
		paceBase.current && !fixingPass
			? estimateTimeLeft(paceBase.current, Date.now(), progress.done, progress.total)
			: null;

	// Live-activity strip — the newest real event line across the run.
	// # DECISION: remember the last line seen while mounted.
	// # WHY: the per-section feed clears on section_ready / pipeline_complete,
	// #      so without memory the strip would blank at the very moment the
	// #      run ends ("Reviewer pass complete"). Cleared when a new run starts
	// #      preparing (init panel) or the spine empties.
	const lastActivityRef = useRef<AgentActivityLine | null>(null);
	const liveActivity =
		latestRunActivity !== undefined ? latestRunActivity : latestActivityAcross(agentActivityBySection);
	if (initStatus || sections.length === 0) lastActivityRef.current = null;
	if (liveActivity) lastActivityRef.current = liveActivity;
	const stripLine = liveActivity ?? lastActivityRef.current;

	// "Now drafting" card — the running section (the active row when it runs,
	// else the first running row), its stage and its live log.
	// # DECISION: the stepper's stage is remembered per running section and
	// #   only moves forward; it is dropped once the section stops running.
	// # WHY: the feed is a 6-line ring buffer and a drafter re-reads sources
	// #      mid-draft, so a stage read off the newest lines alone would jump
	// #      back from Drafting to Sources. Only real signals advance it
	// #      (status / audit phase, subline wording, feed stage tags); with
	// #      none yet the row draws no stage strip rather than guessing.
	// # DATE: 2026-10-14
	const stepMemoRef = useRef<Map<string, DraftStep>>(new Map());
	const runningRows = sections.filter(isRunning);
	const stepMemo = stepMemoRef.current;
	for (const id of [...stepMemo.keys()]) {
		if (!runningRows.some((r) => r.id === id)) stepMemo.delete(id);
	}
	for (const r of runningRows) {
		const step = deriveDraftStep(r, agentActivityBySection?.get(r.id), stepMemo.get(r.id) ?? null);
		if (step) stepMemo.set(r.id, step);
	}
	// # DECISION: show each section's total time and the run's elapsed / total
	// #   time, from when this pane saw each section start and settle.
	// # WHY: stage timers reset per stage, so nothing said how long a section
	// #      or the whole run took (Guru, 2026-10-14). The stream carries no
	// #      section start time, so a section's start is the earlier of first
	// #      sight running and its current stage start; a section already done
	// #      before the pane opened shows no total. The finished run's total
	// #      prefers the run summary's duration.
	// # DATE: 2026-10-14
	// Number column sized to the longest number shown, not a fixed 62px:
	// "5.1" next to a CMC-wide column left a big gap (Guru 2026-10-14).
	const gutterChars = Math.max(1, ...sections.map((s) => formatGutterNumber(s.number).length));
	const gutterStyle = { width: `min(62px, calc(${gutterChars}ch + 10px))` };
	const timingRef = useRef<Map<string, { start: number; end?: number; lastEnd?: number }>>(new Map());
	const timing = timingRef.current;
	const clock = Date.now();
	for (const s of sections) {
		const t = timing.get(s.id);
		if (isRunning(s)) {
			// A section runs again after it settles (the review pass starts once
			// the draft is accepted): keep its first start so the total covers
			// the whole section, and resume the clock. Restarting here is what
			// showed "0s" on every finished row (Guru 2026-10-14).
			if (!t) timing.set(s.id, { start: Math.min(clock, s.sublineStartedAt ?? clock) });
			else if (t.end !== undefined) timing.set(s.id, { start: t.start, lastEnd: t.end });
		} else if (t && t.end === undefined) {
			if (s.status === "queued") timing.delete(s.id);
			else
				timing.set(s.id, {
					start: t.start,
					end: Math.max(s.acceptedAt && s.acceptedAt >= t.start ? s.acceptedAt : clock, t.lastEnd ?? 0),
				});
		}
	}
	let runStart: number | null = null;
	let runEnd: number | null = null;
	for (const t of timing.values()) {
		if (runStart === null || t.start < runStart) runStart = t.start;
		if (t.end !== undefined && (runEnd === null || t.end > runEnd)) runEnd = t.end;
	}
	const sectionById = new Map(sections.map((s) => [s.id, s]));
	const sectionTotal = (id: string, nowMs: number): number | null => {
		// The server's own start and settle times when the stream gave them:
		// the pane's sightings drift with late or stray events.
		const s = sectionById.get(id);
		if (s?.ranFrom !== undefined) {
			if (s.ranTo !== undefined && s.ranTo >= s.ranFrom) return s.ranTo - s.ranFrom;
			if (isRunning(s)) return Math.max(0, nowMs - s.ranFrom);
		}
		const t = timing.get(id);
		if (!t) return null;
		return (t.end ?? nowMs) - t.start;
	};

	// # DECISION: live detail sits IN each running row (stage strip + newest
	// #   agent action), not in cards above the list.
	// # WHY: up to three sections draft in parallel; cards above the list
	// #      repeated those rows and pushed the run's big picture below the
	// #      fold (Guru, 2026-10-14). The list stays the one view.
	// # DATE: 2026-10-14
	const newestLineFor = (id: string): AgentActivityLine | null => {
		const lines = agentActivityBySection?.get(id);
		return lines && lines.length > 0 ? lines[lines.length - 1] : null;
	};
	const anyRowLog = runningRows.some((r) => newestLineFor(r.id) !== null);

	// Live elapsed-time tick for the active row's subline. One shared
	// tick state at the root drives all rows' elapsed displays — N
	// active sections (rare) re-render together rather than each
	// wiring its own interval. Pauses when no section is active.
	// Cadence is 1000ms — the elapsed display is minute-resolution, so
	// anything faster just thrashes the spine's render tree (and
	// re-runs descendant effects) without the writer seeing any
	// benefit. The earlier 250ms cadence was a pre-2026-05 default
	// that contributed to the "Word feels laggy when the pane is open"
	// symptom alongside the Office.js round-trip pile-up.
	const [now, setNow] = useState(() => Date.now());
	// Also tick while a section is being written into Word — those trackers can
	// run post-run (no active section) and still show a live elapsed reading.
	const loadingIntoWord = !!liveDocLoadBySection && hasActiveLiveDocLoad(liveDocLoadBySection);
	const anyActivity = hasAnyActivity(agentActivityBySection);
	useEffect(() => {
		if ((!activeSectionId && !loadingIntoWord && !anyActivity) || pauseAnimations) return;
		const t = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(t);
	}, [activeSectionId, loadingIntoWord, anyActivity, pauseAnimations]);

	// Auto-scroll the active row into view as drafting advances. The
	// design fixture fits every section in one pane so never had to
	// address this; real runs (38-section CSR) overflow, and without
	// a scroll cue the halo pulse disappears from view the moment
	// drafting moves past the visible range. ``block: "nearest"`` only
	// scrolls when the row is actually off-screen — no jitter when it
	// was already in view.
	//
	// # DECISION (2026-05-06): suppress auto-scroll for 3s after the
	// # writer manually scrolls. Pre-2026-05-06 the effect re-fired on
	// # every activeSectionId change, which yanked the writer's view
	// # back whenever they tried to look at an earlier section — the
	// # auto-scroll fought the writer's intent. The grace window means:
	// # if the writer scrolls, we leave them alone for 3s; if drafting
	// # advances during that window, the halo pulse may go off-screen
	// # but the writer keeps control. After 3s of no manual scroll,
	// # auto-follow resumes.
	// # WHY: writer agency > follow-the-agent affordance. A surprising
	// # auto-scroll is the worst kind of ambient UI — it makes the
	// # writer feel like the app is fighting them.
	const activeRowRef = useRef<HTMLButtonElement | null>(null);
	const viewportRef = useRef<HTMLDivElement | null>(null);
	const lastUserScrollRef = useRef<number>(0);
	const programmaticScrollUntilRef = useRef<number>(0);
	const [isActiveRowVisible, setIsActiveRowVisible] = useState(true);

	const recomputeActiveRowVisibility = useCallback(() => {
		if (!activeSectionId) {
			setIsActiveRowVisible(true);
			return;
		}
		const viewportEl = viewportRef.current;
		const activeRowEl = activeRowRef.current;
		if (!viewportEl || !activeRowEl) {
			setIsActiveRowVisible(false);
			return;
		}
		const viewportRect = viewportEl.getBoundingClientRect();
		const rowRect = activeRowEl.getBoundingClientRect();
		const intersects = rowRect.bottom > viewportRect.top && rowRect.top < viewportRect.bottom;
		setIsActiveRowVisible(intersects);
	}, [activeSectionId]);

	// Track manual scrolls. We can't perfectly distinguish a user wheel
	// event from a programmatic scrollIntoView, but we can record when
	// we KNOW we just initiated a programmatic scroll — any onScroll
	// event within ~600ms of that is probably the smooth-scroll
	// animation, not the user. Beyond that window, treat onScroll as
	// user intent.
	const handleScroll = useCallback(() => {
		if (Date.now() < programmaticScrollUntilRef.current) return;
		lastUserScrollRef.current = Date.now();
		recomputeActiveRowVisibility();
	}, [recomputeActiveRowVisibility]);

	useEffect(() => {
		recomputeActiveRowVisibility();
	}, [recomputeActiveRowVisibility, activeSectionId, sections]);

	useEffect(() => {
		const handleResize = () => recomputeActiveRowVisibility();
		globalThis.addEventListener("resize", handleResize);
		return () => globalThis.removeEventListener("resize", handleResize);
	}, [recomputeActiveRowVisibility]);

	useEffect(() => {
		if (!activeSectionId || !activeRowRef.current) return;
		const ts = Date.now();
		// Skip if the writer scrolled within the last 3s — they're driving.
		if (ts - lastUserScrollRef.current < 3000) return;
		programmaticScrollUntilRef.current = ts + 600;
		activeRowRef.current.scrollIntoView({
			block: "nearest",
			behavior: "smooth",
		});
		const t = globalThis.setTimeout(() => recomputeActiveRowVisibility(), 650);
		return () => globalThis.clearTimeout(t);
	}, [activeSectionId, recomputeActiveRowVisibility]);

	const handleFloatingActiveClick = useCallback(() => {
		if (!activeSection) return;
		onSectionFocus?.(activeSection.id);
		const row = activeRowRef.current;
		if (!row) return;
		const ts = Date.now();
		programmaticScrollUntilRef.current = ts + 600;
		row.scrollIntoView({ block: "nearest", behavior: "smooth" });
		globalThis.setTimeout(() => recomputeActiveRowVisibility(), 650);
	}, [activeSection, onSectionFocus, recomputeActiveRowVisibility]);

	const showFloatingActiveRow = activeSection !== null && activeSection.status === "drafting" && !isActiveRowVisible;

	// Init checklist — derived fresh from the latest init event (pure,
	// no accumulation). Null checklist = unknown stage (older server) →
	// message-only fallback banner. The message-only path still shows the
	// progress bar when the event carries counts (activation resolution).
	const initChecklist = initStatus ? deriveInitChecklist(initStatus) : null;
	const initActiveProgress =
		initChecklist?.find((step) => step.state === "active")?.progress ??
		(initStatus && typeof initStatus.done === "number" && typeof initStatus.total === "number" && initStatus.total > 0
			? { done: initStatus.done, total: initStatus.total }
			: null);

	return (
		<div className={styles.root}>
			<div ref={viewportRef} className={styles.viewport} onScroll={handleScroll} data-spine-viewport="true">
				{streamError && <div className={styles.errorBanner}>{streamError}</div>}
				{!streamError && initStatus && (
					<div className={styles.initPanel}>
						<div className={styles.initPanelTitle}>
							<span className={styles.initBannerIcon}>⧗</span>
							Preparing run
						</div>
						{initChecklist === null ? (
							<div className={styles.initStepLabel}>{initStatus.message}</div>
						) : (
							initChecklist.map((step) => (
								<div key={step.id} className={styles.initStep}>
									<span
										aria-hidden="true"
										className={mergeClasses(
											styles.initStepMarker,
											step.state === "done" && styles.initStepMarkerDone,
											step.state === "active" && styles.initStepMarkerActive,
										)}
									/>
									<span className={step.state === "pending" ? styles.initStepPending : styles.initStepLabel}>
										{step.label}
									</span>
									{step.progress && (
										<span className={styles.initStepCount}>
											{step.progress.done}/{step.progress.total}
										</span>
									)}
								</div>
							))
						)}
						{initActiveProgress && (
							<div className={styles.initBar}>
								<div
									className={styles.initBarFill}
									style={{
										width: `${(initActiveProgress.done / Math.max(1, initActiveProgress.total)) * 100}%`,
									}}
								/>
							</div>
						)}
						{initSideNote && <div className={styles.initSideNote}>{initSideNote}</div>}
					</div>
				)}

				{header}

				{sections.length > 0 && !header && complete && progress.phase === "drafted" && (
					<RunCompleteCard
						sections={sections}
						durationMs={runDurationMs}
						issuesOpen={complete.issuesOpen}
						onReviewIssues={complete.onReviewIssues}
					/>
				)}

				{sections.length > 0 && !header && !(complete && progress.phase === "drafted") && (
					<section className="as-spinehead" data-testid="spine-header">
						<div className="as-spinehead-top">
							<h1 className="as-spinehead-title">
								{fixingPass
									? fixingHeader(fixingFlagCount, fixingModuleIds?.size ?? 0)
									: progress.label.replace(" · ", " ")}
								{progress.failed > 0 && <span className={styles.headerFailedMeta}>{progress.failed} failed</span>}
							</h1>
							{(() => {
								const finished = progress.phase === "drafted" || progress.phase === "failed";
								const totalMs = finished
									? runDurationMs && runDurationMs > 0
										? runDurationMs
										: runStart !== null && runEnd !== null
											? runEnd - runStart
											: null
									: runStart !== null
										? now - runStart
										: null;
								if (totalMs === null) return null;
								// # DECISION: the header time is measured by this pane unless it
								// #   comes from the run summary; the tooltip says so.
								// # WHY: the stream has no run start time, so the clock starts when
								// #      the pane first sees a section running; a closed pane, a
								// #      sleeping laptop or opening mid-run skews it (Guru 2026-10-14).
								// # DATE: 2026-10-14
								const fromServer = finished && !!runDurationMs && runDurationMs > 0;
								const note = fromServer
									? "Total time for the run"
									: "Approximate: timed by this pane from when it first saw drafting. It can be off if the pane was closed, the laptop slept, or the run started before the pane opened.";
								return (
									<span className="as-spinehead-time" data-testid="spine-header-time" title={note}>
										{formatTotal(totalMs)}
									</span>
								);
							})()}
						</div>
						{(() => {
							const ofTemplate =
								templateSectionCount && templateSectionCount > progress.total
									? `${progress.total} of ${templateSectionCount} template sections`
									: "";
							const line = fixingPass ? "" : [progress.summary, timeLeft, ofTemplate].filter(Boolean).join(" · ");
							return (
								<ProgressBar
									value={progress.done}
									total={Math.max(1, progress.total)}
									label={<span data-testid="spine-header-summary">{line}</span>}
								/>
							);
						})()}
						{controls}
						{/* Run-level strip — only when no running row has a live line to
						    show (between modules, or before the first event). Hidden once
						    the run has finished, and never for internal orchestration beats
						    ("Superintendent complete") that tell the writer nothing
						    (Guru 2026-10-14). */}
						{stripLine &&
							!anyRowLog &&
							progress.phase !== "drafted" &&
							progress.phase !== "failed" &&
							!/^(superintendent|lead planner|planner|orchestrator)\b/i.test(stripLine.text) && (
								<div
									className={mergeClasses(
										styles.activityStrip,
										stripLine.kind === "error" && styles.activityStripError,
									)}
									aria-live="polite"
									title={stripLine.text}
									data-testid="spine-activity-strip"
								>
									{stripLine.text}
								</div>
							)}
					</section>
				)}

				{sections.length === 0 && <div className={styles.emptyHint}>Waiting for the first section to start…</div>}

				{sections.map((section) => {
					const isActive = section.id === activeSectionId;
					const showPopoutHere = pendingApproval?.sectionId === section.id && onApprovalAccept && onApprovalOpenReview;
					// Hierarchy: indent the TITLE only, by depth of the display
					// number ("9.3.1" → 2 steps). The number column stays put so
					// the numbers line up; the taskpane is narrow, hence the
					// small step.
					const titleIndent = (spineDepth(section.number) - 1) * SPINE_INDENT_PX;
					// Something to open: a running row (live history) or any settled draft.
					const openable =
						isRunning(section) ||
						section.status === "drafted" ||
						section.status === "needs_approval" ||
						section.status === "flagged";
					const chip = chipFor(section, Boolean(fixingModuleIds?.has(section.id)));
					const drafted = section.status === "drafted" ? formatDraftedSubline(section) : null;
					const step = isRunning(section) ? (stepMemo.get(section.id) ?? null) : null;
					const stepIdx = step ? DRAFT_STEPS.findIndex((st) => st.id === step) : -1;
					// Say each thing once (Guru 2026-10-14): the pill names the stage,
					// so the subline drops its leading stage word and keeps the detail.
					const metaText = section.subline && stepIdx >= 0 ? sublineDetail(section.subline) : section.subline;
					const newest = isLive(section) ? newestLineFor(section.id) : null;
					// The newest action line often repeats the subline word for word
					// ("locating …" twice); then the meta keeps only the pill.
					// Between tool calls the subline reads "thinking · last: <that
					// action>", which the newest line already shows; compare without it.
					const norm = (t: string) =>
						t
							.replace(/^(\[\d+\/\d+\]\s*)?thinking · last:\s*/iu, "")
							.replace(/[…\s]+$/u, "")
							.trim()
							.toLowerCase();
					const repeatsLog = !!newest && !!metaText && norm(lineBody(newest)).startsWith(norm(metaText).slice(0, 40));
					let meta: string | null = null;
					if (isLive(section) && metaText && !repeatsLog) meta = metaText;
					else if (isActive && section.status === "needs_approval") meta = "needs your call";
					else if (drafted) meta = drafted;
					const ms = sectionTotal(section.id, now);
					const pill: { status: PillStatus; label: string } =
						stepIdx >= 0 && step !== "done"
							? { status: step === "reviewing" ? "review" : "drafting", label: STAGE_LABEL[step as DraftStep] }
							: chip;
					const ld = liveDocLoadBySection?.get(section.id);

					return (
						<React.Fragment key={section.id}>
							<button
								type="button"
								ref={isActive ? activeRowRef : undefined}
								className={mergeClasses("as-srow", !openable && styles.rowInert)}
								onClick={() => onSectionFocus?.(section.id)}
								aria-current={isActive ? "true" : undefined}
								// Every running row is shaded, not just the one the pane follows.
								data-running={isLive(section) ? "true" : undefined}
								aria-label={`${formatGutterNumber(section.number) ? `Section ${section.number}: ` : ""}${section.title} (${section.status})`}
								title={openable ? "Open section details" : undefined}
								data-active-row={isActive ? "true" : undefined}
								data-openable={openable ? "true" : undefined}
							>
								<span className="as-srow-num" data-gutter>
									{formatGutterNumber(section.number)}
								</span>
								<span style={{ minWidth: 0, paddingLeft: titleIndent > 0 ? `${titleIndent}px` : undefined }}>
									<span className="as-srow-title">{section.title}</span>
									<span className="as-srow-sub" data-testid="spine-row-meta">
										<span
											className={mergeClasses(`as-pill as-pill-${pill.status}`, stepIdx >= 0 && step !== "done" && "as-pill-staged")}
											data-chip={chip.label}
										>
											{stepIdx >= 0 && step !== "done" && (
												<span
													className={styles.rowSteps}
													data-testid="spine-row-steps"
													title={`Stage: ${DRAFT_STEPS[stepIdx].label}`}
												>
													{/* Three working stages only: a fourth "Done" dash could never light
													    up here, because a finished row leaves the running state. */}
													{DRAFT_STEPS.filter((st) => st.id !== "done").map((st, k) => (
														<span
															key={st.id}
															data-step-state={k < stepIdx ? "done" : k === stepIdx ? "active" : "pending"}
															className={mergeClasses(
																styles.rowStep,
																k < stepIdx && styles.rowStepDone,
																k === stepIdx && styles.rowStepActive,
															)}
														/>
													))}
												</span>
											)}
											{pill.label}
										</span>
									</span>
									{meta && (
										<span className="as-srow-step as-srow-line" title={section.subline ?? meta}>
											{meta}
											{/* Stage timer only once it differs from the section's own
											    total on the right; in the first stage they are the same
											    number and showing both read as "25s 25s". */}
											{isRunning(section) &&
												section.sublineStartedAt &&
												Math.abs((ms ?? 0) - (now - section.sublineStartedAt)) >= 1000 && (
													<span className={styles.sublineElapsed} title="Time in this stage">
														{" · "}
														{formatElapsed(now - section.sublineStartedAt)}
													</span>
												)}
										</span>
									)}
									{newest && (
										<span
											className={mergeClasses("as-srow-step as-srow-line", newest.kind === "error" && styles.activityLineError)}
											title={newest.text}
											data-testid="spine-row-log"
										>
											{lineBody(newest)}
											<span className={styles.sublineElapsed}> · {formatElapsed(now - newest.at)}</span>
										</span>
									)}
									{/* Custom submission: which template section this heading was
									    matched to (persisted on the spine so it survives reload). */}
									{section.matchedTitle && (
										<span className="as-srow-step as-srow-line">Template: {section.matchedTitle}</span>
									)}
									{/* Per-section "loading into Word…" tracker (post-redraft
									    live-doc write), driven by shell state so it survives the
									    SSE subline wipe. */}
									{ld?.state === "loading" && (
										<span className="as-srow-step as-srow-line">
											{/* A fill/review already narrates on the meta line above. */}
											{section.auditPhase === "done" && section.subline && !section.postDraftTask
												? section.subline
												: "loading into Word…"}
											<span className={styles.sublineElapsed}> · {formatElapsed(now - ld.startedAt)}</span>
										</span>
									)}
									{ld?.state === "loaded" && <span className="as-srow-step as-srow-line">Loaded into Word</span>}
									{ld?.state === "failed" && (
										<span className={mergeClasses("as-srow-step as-srow-line", styles.sublineLoadFailed)}>
											couldn't load into Word — use Load
										</span>
									)}
								</span>

								<span className="as-srow-end">
									{section.status === "drafted" && section.quality === "low" && (
										<span className={mergeClasses(styles.rightMeta, styles.rightMetaLow)}>LOW</span>
									)}
									{section.status === "drafted" && !!section.contentGapCount && section.contentGapCount > 0 && (
										<span className="as-flag" title={`${section.contentGapCount} gaps in the draft`}>
											<Icon name="flag" />
											{section.contentGapCount}
										</span>
									)}
									{/* Regulatory-audit flag count — persisted by the audit and
									    surfaced on reopen. Clicking it opens the section's Review tab. */}
									{section.regulatorFlagCount !== undefined && section.regulatorFlagCount > 0 && (
										<span
											className={mergeClasses(styles.rightMeta, styles.rightMetaRegulator)}
											title="View regulatory review findings"
											role={onOpenReview ? "button" : undefined}
											tabIndex={onOpenReview ? 0 : undefined}
											aria-label={onOpenReview ? `View ${section.regulatorFlagCount} regulatory findings` : undefined}
											style={onOpenReview ? { cursor: "pointer" } : undefined}
											onClick={
												onOpenReview
													? (e) => {
															e.stopPropagation();
															onOpenReview(section.id);
														}
													: undefined
											}
											onKeyDown={
												onOpenReview
													? (e) => {
															if (e.key === "Enter" || e.key === " ") {
																e.preventDefault();
																e.stopPropagation();
																onOpenReview(section.id);
															}
														}
													: undefined
											}
										>
											{section.regulatorFlagCount}⚖
										</span>
									)}
									{section.status === "drafted" && section.flaggedCount !== undefined && section.flaggedCount > 0 && (
										<span className={mergeClasses(styles.rightMeta, styles.rightMetaFindings)}>
											{section.flaggedCount}⚐
										</span>
									)}
									{countersBySection &&
										(() => {
											const counters = countersBySection.get(section.id);
											if (!counters) return null;
											const showStar = section.status === "drafted" && section.quality === "high" && counters.regens > 0;
											return <SpineTagStrip counters={counters} showStar={showStar} />;
										})()}
									{ms === null ? (
										// Reopened run: the pane never timed it, so show when the
										// section was ready, counted from the run's start (saved
										// run data, no extra fetch). Guru 2026-10-14.
										section.readyAfterMs !== undefined && !isRunning(section) ? (
											<span
												data-testid="spine-row-ready"
												title={`Ready ${formatTotal(section.readyAfterMs)} after the run started (from the saved run)`}
											>
												+{formatTotal(section.readyAfterMs)}
											</span>
										) : null
									) : (
										<span
											data-testid="spine-row-total"
											title={isRunning(section) ? "Time on this section so far" : "Time this section took"}
										>
											{formatTotal(ms)}
										</span>
									)}
									<SpineStatusMark
										status={section.status}
										failed={section.failed}
										failureReason={section.failureReason}
										auditing={isLive(section)}
										paused={pauseAnimations}
									/>
								</span>
							</button>
							{showPopoutHere && pendingApproval && (
								<ApprovalPopout
									approval={pendingApproval}
									onAccept={onApprovalAccept}
									onOpenReview={onApprovalOpenReview}
								/>
							)}
						</React.Fragment>
					);
				})}
				{afterRows}
			</div>

			{footerNote && !header && progress.phase !== "drafted" && progress.phase !== "failed" && sections.length > 0 && (
				<p className="as-footnote">{footerNote}</p>
			)}

			{showFloatingActiveRow && activeSection && (
				<div className={styles.floatingDock}>
					<button
						type="button"
						className={mergeClasses("as-srow", styles.floatingRow)}
						onClick={handleFloatingActiveClick}
						aria-label={`Jump to active section ${formatGutterNumber(activeSection.number) ? `${activeSection.number}: ` : ""}${activeSection.title}`}
						data-testid="floating-active-section-row"
					>
						<span className="as-srow-num">
							{formatGutterNumber(activeSection.number)}
						</span>
						<span style={{ minWidth: 0 }}>
							<span className="as-srow-title">{activeSection.title}</span>
							{(() => {
								const feed = agentActivityBySection?.get(activeSection.id);
								const latest = feed && feed.length > 0 ? feed[feed.length - 1] : null;
								if (latest) {
									return (
										<span className={mergeClasses(styles.subline, latest.kind === "error" && styles.activityLineError)}>
											<span className={styles.sublineDot}>●</span>
											{latest.text}
											<span className={styles.sublineElapsed}>
												&nbsp;·&nbsp;
												{formatElapsed(now - latest.at)}
											</span>
										</span>
									);
								}
								return activeSection.subline ? (
									<span className={styles.subline}>
										<span className={styles.sublineDot}>●</span>
										{activeSection.subline}
										{activeSection.sublineStartedAt && (
											<span className={styles.sublineElapsed}>
												&nbsp;·&nbsp;
												{formatElapsed(now - activeSection.sublineStartedAt)}
											</span>
										)}
									</span>
								) : null;
							})()}
						</span>
						<SpineStatusMark
							status={activeSection.status}
							failed={activeSection.failed}
							failureReason={activeSection.failureReason}
							auditing={activeSection.auditPhase === "running"}
							paused={pauseAnimations}
						/>
					</button>
				</div>
			)}
		</div>
	);
};


/** Run complete summary (design board 14). Pure presentation. */
const RunCompleteCard: React.FC<{
	sections: readonly SpineSection[];
	durationMs?: number;
	issuesOpen: number | null;
	onReviewIssues?: () => void;
}> = ({ sections, durationMs, issuesOpen, onReviewIssues }) => {
	const drafted = sections.filter(
		(s) => s.status === "drafted" || s.status === "needs_approval" || (s.status === "flagged" && !s.failed),
	).length;
	const citations = sections.reduce((n, s) => n + (s.citationCount ?? 0), 0);
	return (
		<section className="as-runcard" data-testid="spine-run-complete">
			<div className="as-runcard-top">
				<span className="as-runcard-label">Run complete</span>
				{durationMs && durationMs > 0 ? <span className="as-spinehead-time">{formatTotal(durationMs)}</span> : null}
			</div>
			<h1 className="as-runcard-title">
				{drafted === 1 ? "1 section is in your document" : `${drafted} sections are in your document`}
			</h1>
			<div className="as-runcard-stats">
				<div className="as-runcard-stat">
					<b>{drafted}</b>
					<span>drafted</span>
				</div>
				<div className="as-runcard-stat">
					<b>{citations}</b>
					<span>{citations === 1 ? "citation" : "citations"}</span>
				</div>
				{issuesOpen !== null && (
					<div className="as-runcard-stat">
						<b>{issuesOpen}</b>
						<span>{issuesOpen === 1 ? "issue open" : "issues open"}</span>
					</div>
				)}
			</div>
			{onReviewIssues && issuesOpen !== null && issuesOpen > 0 && (
				<Button variant="primary" block onClick={onReviewIssues}>
					{issuesOpen === 1 ? "Review 1 issue" : `Review ${issuesOpen} issues`}
				</Button>
			)}
		</section>
	);
};

export default LoomSpine;
